/**
 * "All" option row for mobile multi-select dropdowns.
 *
 * Renders a pressable row that clears all selections, visually matching
 * the MobileFilterOption style. Shows a check icon when nothing is selected
 * (i.e. "All" is the active state).
 * @module components/shared/MobileFilterSheet/MobileSelectAllOption/MobileSelectAllOptionView
 */

import { Check } from "lucide-react-native";
import { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for MobileSelectAllOptionView */
interface MobileSelectAllOptionViewProps {
  /** Label for the "all" state, e.g. "All Positions" */
  readonly label: string;
  /** Whether nothing is selected (i.e. "All" is active) */
  readonly isActive: boolean;
  /** Text color for active state */
  readonly textColor: string;
  /** Text color for inactive state */
  readonly textMutedColor: string;
  /** Callback when pressed — should clear all selections */
  readonly onPress: () => void;
}

/**
 * Select-all label color style.
 * design-system: dynamic-style waiver — data-driven value (active/muted theme colors)
 * @param isActive - Whether the option is active
 * @param textColor - Active text color
 * @param textMutedColor - Inactive text color
 */
const selectAllTextColorStyle = (
  isActive: boolean,
  textColor: string,
  textMutedColor: string
) => ({ color: isActive ? textColor : textMutedColor });

/**
 * Renders the "All" option row with check icon when active.
 * @param root0 - Component props
 * @param root0.label - Display label
 * @param root0.isActive - Whether "All" is selected
 * @param root0.textColor - Active text color
 * @param root0.textMutedColor - Inactive text color
 * @param root0.onPress - Press handler
 */
const MobileSelectAllOptionView = ({
  label,
  isActive,
  textColor,
  textMutedColor,
  onPress,
}: MobileSelectAllOptionViewProps) => (
  <Stack className="px-2">
    <Pressable
      onPress={onPress}
      className="flex-row items-center justify-between px-1 py-3"
    >
      <Text
        variant="unstyled"
        className={`text-title-sm ${isActive ? "font-heading" : ""}`}
        // eslint-disable-next-line no-restricted-syntax -- data-driven theme colors (textColor/textMutedColor arbitrary string props from parent portal-safe palette; no fixed token set)
        UNSAFE_style={selectAllTextColorStyle(
          isActive,
          textColor,
          textMutedColor
        )}
      >
        {label}
      </Text>
      {isActive ? <Icon as={Check} size="sm" color={textColor} /> : null}
    </Pressable>
  </Stack>
);

MobileSelectAllOptionView.displayName = "MobileSelectAllOptionView";

export default memo(MobileSelectAllOptionView);
